import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["navItem", "expandableContainer", "expandableButton", "nestedContainer", "caret"]
  static values = { 
    activeClass: String,
    inactiveClass: String 
  }

  connect() {
    this.initializeExpandedStates()
    this.updateActiveStates()

    // Bind handlers once and keep the references so disconnect() can remove the
    // exact same functions. Previously these were inline arrow functions with no
    // teardown, so every Turbo navigation re-connected the controller and leaked
    // another set of window listeners — updateActiveStates then fired more times
    // per event the longer the session ran (a progressive slowdown). Nav is now
    // plain Turbo Drive page visits, which tear down and reconnect this
    // controller on every navigation, so removing the listeners on disconnect()
    // matters more than ever.
    this.boundUpdateActiveStates = () => this.updateActiveStates()
    this.boundDeferredUpdateActiveStates = () => setTimeout(() => this.updateActiveStates(), 100)

    // Re-derive the active highlight on navigation. The server already renders
    // the correct active item on each full page visit; these listeners keep the
    // highlight in sync for history restores and any in-page frame updates.
    // (The former turbo:main-content-loaded listener was removed: nothing
    // dispatches that event.)
    window.addEventListener('turbo:url-updated', this.boundUpdateActiveStates)
    window.addEventListener('turbo:history-restore', this.boundUpdateActiveStates)
    window.addEventListener('turbo:frame-load', this.boundUpdateActiveStates)
    window.addEventListener('turbo:before-visit', this.boundDeferredUpdateActiveStates)
    window.addEventListener('turbo:visit', this.boundDeferredUpdateActiveStates)
  }

  disconnect() {
    // Remove every window listener connect() added. Without this the listeners
    // accumulate across the session (see connect()).
    window.removeEventListener('turbo:url-updated', this.boundUpdateActiveStates)
    window.removeEventListener('turbo:history-restore', this.boundUpdateActiveStates)
    window.removeEventListener('turbo:frame-load', this.boundUpdateActiveStates)
    window.removeEventListener('turbo:before-visit', this.boundDeferredUpdateActiveStates)
    window.removeEventListener('turbo:visit', this.boundDeferredUpdateActiveStates)
  }

  initializeExpandedStates() {
    this.expandableContainerTargets.forEach(container => {
      const itemName = container.dataset.sidebarItemName
      const savedState = localStorage.getItem(`sidebar_expanded_${itemName}`)
      const currentPageExpanded = container.dataset.sidebarExpanded === 'true'
      
      // Priority: saved state > current page active state
      const shouldExpand = savedState !== null ? savedState === 'true' : currentPageExpanded
      
      this.setExpandedState(container, shouldExpand, false) // false = no animation on init
    })
  }

  toggleExpand(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const button = event.currentTarget
    const container = button.closest('[data-sidebar-target="expandableContainer"]')
    const nestedContainer = container.querySelector('[data-sidebar-target="nestedContainer"]')
    const isExpanded = nestedContainer.style.maxHeight && nestedContainer.style.maxHeight !== '0px'
    
    this.setExpandedState(container, !isExpanded, true) // true = with animation
    
    // Save state
    const itemName = container.dataset.sidebarItemName
    localStorage.setItem(`sidebar_expanded_${itemName}`, (!isExpanded).toString())
  }

  setExpandedState(container, isExpanded, animate) {
    const nestedContainer = container.querySelector('[data-sidebar-target="nestedContainer"]')
    const caret = container.querySelector('[data-sidebar-target="caret"]')
    
    const currentMaxHeight = nestedContainer.style.maxHeight
    const currentlyExpanded = !currentMaxHeight || (currentMaxHeight !== '0px' && currentMaxHeight !== '0')

    if (currentlyExpanded === isExpanded) {
      return // Already in desired state
    }

    if (isExpanded) {
      nestedContainer.style.maxHeight = nestedContainer.scrollHeight + 'px'
      if (caret) caret.style.transform = 'rotate(180deg)'
    } else {
      nestedContainer.style.maxHeight = '0'
      if (caret) caret.style.transform = 'rotate(0deg)'
    }
  }

  handleNavigation(event) {
    // Update active states when navigation occurs
    this.updateActiveStates()
    
    // Optional: Add loading state
    const clickedItem = event.currentTarget
    this.addLoadingState(clickedItem)
  }

  updateActiveStates() {
    const currentPath = window.location.pathname
    
    this.navItemTargets.forEach(item => {
      const itemPath = item.dataset.sidebarPath || item.getAttribute('href')
      const isActive = this.isActivePath(currentPath, itemPath)
      
      if (isActive) {
        item.classList.add('active')
      } else {
        item.classList.remove('active')
      }
    })

    this.expandableContainerTargets.forEach(container => {
      const button = container.querySelector('[data-sidebar-target="expandableButton"]')
      const hasActiveNested = container.querySelector('.sidebar-nav-item-nested.active')
      
      if (hasActiveNested) {
        button.classList.add('active')
        this.setExpandedState(container, true, false)
      } else {
        button.classList.remove('active')
      }
    })
  }

  isActivePath(currentPath, itemPath) {
    if (!itemPath) return false
    
    // Handle root/dashboard paths
    if (itemPath === '/dashboard') {
      return currentPath === '/dashboard' || currentPath === '/'
    }
    
    // Handle exact matches
    if (currentPath === itemPath) {
      return true
    }
    
    if (itemPath === '/leads/inbox' || itemPath.includes('/leads/inbox')) {
      return currentPath === '/leads/inbox' || currentPath.startsWith('/leads/inbox/')
    }

    if (itemPath === '/leads') {
      return currentPath === '/leads' || (currentPath.startsWith('/leads?'))
    }

    // Settings owns /settings but not the Suppression subtrees (/settings/blacklist
    // and /settings/suppression), which
    // belongs to the Suppression nav item.
    if (itemPath === '/settings') {
      return currentPath === '/settings' ||
        (currentPath.startsWith('/settings/') && !currentPath.startsWith('/settings/blacklist') && !currentPath.startsWith('/settings/suppression'))
    }

    if (itemPath !== '/leads' && currentPath.startsWith(itemPath + '/')) {
      return true
    }

    if (currentPath.startsWith(itemPath + '?')) {
      return true
    }
    
    // Handle special routing cases
    if (currentPath.startsWith('/lead-scoring') && itemPath.includes('lead-scoring')) {
      return true
    }
    
    // Handle admin paths
    if (currentPath.startsWith('/admin/configuration') && itemPath.includes('configuration')) {
      return true
    }
    
    if (currentPath.startsWith('/admin/client_onboarding') && itemPath.includes('client_onboarding')) {
      return true
    }
    
    return false
  }


  addLoadingState(item) {
    // Add a subtle loading indicator
    item.classList.add('opacity-75')
    
    // Remove loading state after a short delay
    setTimeout(() => {
      item.classList.remove('opacity-75')
    }, 200)
  }
} 